/**
 * Store Health CrossFrame Listeners
 *
 * Handles CrossFrame requests for Store Health operations.
 * Validates required parameters and delegates to the gateway client via getServiceClient().
 */

import { getServiceClient } from '@api/client-factory/StoreManagementClientFactory'

import {
    GetIssuesForStoreInput,
    GetIssuesForStoreOutput,
    GetTasksForStoreInput,
    GetTasksForStoreOutput,
    GetStoreDetailsInput,
    GetStoreDetailsOutput,
    GetActionItemCountForStoreInput,
    GetActionItemCountForStoreOutput,
    GetStoreCapabilitiesInput,
    GetStoreCapabilitiesOutput,
    GetIssueDetailsInput,
    GetIssueDetailsOutput,
    GetTaskDetailsInput,
    GetTaskDetailsOutput,
    GetIssueClassificationsInput,
    GetIssueClassificationsOutput,
    GetSOPForIssueTypeInput,
    GetSOPForIssueTypeOutput,
    GetActivitiesForParentInput,
    GetActivitiesForParentOutput,
    GetPendingCustomerActionsForParentInput,
    GetPendingCustomerActionsForParentOutput,
    GetPreSignedGetUrlInput,
    GetPreSignedGetUrlOutput,
    GetPreSignedPutUrlInput,
    GetPreSignedPutUrlOutput,
    CreateIssueForStoreInput,
    CreateIssueForStoreOutput,
    ResolveIssueInput,
    ResolveIssueOutput,
    RescheduleTaskInput,
    RescheduleTaskOutput,
    SendMessageInput,
    SendMessageOutput,
    SubmitCustomerActionsResponseInput,
    SubmitCustomerActionsResponseOutput,
} from '@amzn/coral_com-amazon-jwostorehealthservice'
import { validateRequired } from './listenerUtils'

// ========================================
// READ OPERATIONS (ViewStoreHealth)
// ========================================

/**
 * New store-scoped API: Get issues for a specific store.
 * Required: storeId, filters, pagination
 */
export async function getIssuesForStoreListener (message: any): Promise<GetIssuesForStoreOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'filters', 'pagination')
    const client = getServiceClient()
    return client.getIssuesForStore(GetIssuesForStoreInput.clone(params)).toPromise()
}

/**
 * New store-scoped API: Get tasks for a specific store.
 * Required: storeId, filters, pagination
 */
export async function getTasksForStoreListener (message: any): Promise<GetTasksForStoreOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'filters', 'pagination')
    const client = getServiceClient()
    return client.getTasksForStore(GetTasksForStoreInput.clone(params)).toPromise()
}

/**
 * New store-scoped API: Get store details for store health context.
 * Required: storeId
 */
export async function getStoreDetailsListener (message: any): Promise<GetStoreDetailsOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId')
    const client = getServiceClient()
    return client.getStoreDetails(GetStoreDetailsInput.clone(params)).toPromise()
}

/**
 * New store-scoped API: Get action item count for a specific store.
 * Required: storeId
 */
export async function getActionItemCountForStoreListener (message: any): Promise<GetActionItemCountForStoreOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId')
    const client = getServiceClient()
    return client.getActionItemCountForStore(GetActionItemCountForStoreInput.clone(params)).toPromise()
}

/**
 * New store-scoped API: Get the feature capabilities enabled for a specific store.
 * Required: storeId
 */
export async function getStoreHealthCapabilitiesListener (message: any): Promise<GetStoreCapabilitiesOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId')
    const client = getServiceClient()
    return client.getStoreHealthCapabilities(GetStoreCapabilitiesInput.clone(params)).toPromise()
}

/**
 * Get issue details by issue ID.
 * Required: issueId, storeId (storeId added for Gateway AVP authorization)
 */
export async function getIssueDetailsListener (message: any): Promise<GetIssueDetailsOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'issueId', 'storeId')
    const client = getServiceClient()
    return client.getIssueDetails(GetIssueDetailsInput.clone(params)).toPromise()
}

/**
 * Get task details by task ID.
 * Required: taskId, storeId (storeId added for Gateway AVP authorization)
 */
export async function getTaskDetailsListener (message: any): Promise<GetTaskDetailsOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'taskId', 'storeId')
    const client = getServiceClient()
    return client.getTaskDetails(GetTaskDetailsInput.clone(params)).toPromise()
}

/**
 * Get issue classifications.
 * Required: storeId (for Gateway AVP authorization; model has no required fields)
 */
export async function getIssueClassificationsListener (message: any): Promise<GetIssueClassificationsOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId')
    const client = getServiceClient()
    return client.getIssueClassifications(GetIssueClassificationsInput.clone(params)).toPromise()
}

/**
 * Get SOP for a given issue type.
 * Required: storeId, classification
 */
export async function getSOPForIssueTypeListener (message: any): Promise<GetSOPForIssueTypeOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'classification')
    const client = getServiceClient()
    return client.getSOPForIssueType(GetSOPForIssueTypeInput.clone(params)).toPromise()
}

/**
 * Get activities for a parent entity (issue or task).
 * Required: parentId, storeId (storeId added for Gateway AVP authorization)
 */
export async function getActivitiesForParentListener (message: any): Promise<GetActivitiesForParentOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'parentId', 'storeId')
    const client = getServiceClient()
    return client.getActivitiesForParent(GetActivitiesForParentInput.clone(params)).toPromise()
}

/**
 * Get pending customer actions for a parent entity.
 * Required: parentId, storeId (storeId added for Gateway AVP authorization)
 */
export async function getPendingCustomerActionsForParentListener (message: any): Promise<GetPendingCustomerActionsForParentOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'parentId', 'storeId')
    const client = getServiceClient()
    return client.getPendingCustomerActionsForParent(GetPendingCustomerActionsForParentInput.clone(params)).toPromise()
}

/**
 * Get presigned URL for downloading/viewing an attachment.
 * Required: storeId, parentId, activityId
 */
export async function getPreSignedGetUrlListener (message: any): Promise<GetPreSignedGetUrlOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'parentId', 'activityId')
    const client = getServiceClient()
    return client.getPreSignedGetUrl(GetPreSignedGetUrlInput.clone(params)).toPromise()
}

// ========================================
// WRITE OPERATIONS (ManageStoreHealth)
// ========================================

/**
 * Get presigned URL for uploading an attachment.
 * Required: storeId, fileNames
 */
export async function getPreSignedPutUrlListener (message: any): Promise<GetPreSignedPutUrlOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'fileNames')
    const client = getServiceClient()
    return client.getPreSignedPutUrl(GetPreSignedPutUrlInput.clone(params)).toPromise()
}

/**
 * Create a new issue for a store.
 * Required: storeId, classification, description
 */
export async function createIssueForStoreListener (message: any): Promise<CreateIssueForStoreOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'storeId', 'classification', 'description')
    const client = getServiceClient()
    return client.createIssueForStore(CreateIssueForStoreInput.clone(params)).toPromise()
}

/**
 * Resolve an existing issue.
 * Required: issueId, messageInput, storeId (storeId added for Gateway AVP authorization)
 */
export async function resolveIssueListener (message: any): Promise<ResolveIssueOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'issueId', 'messageInput', 'storeId')
    const client = getServiceClient()
    return client.resolveIssue(ResolveIssueInput.clone(params)).toPromise()
}

/**
 * Reschedule a task.
 * Required: taskId, messageInput, storeId (storeId added for Gateway AVP authorization)
 */
export async function rescheduleTaskListener (message: any): Promise<RescheduleTaskOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'taskId', 'messageInput', 'storeId')
    const client = getServiceClient()
    return client.rescheduleTask(RescheduleTaskInput.clone(params)).toPromise()
}

/**
 * Send a message on an issue or task.
 * Required: parentId, parentType, messageInput, storeId (storeId added for Gateway AVP authorization)
 */
export async function sendMessageListener (message: any): Promise<SendMessageOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'parentId', 'parentType', 'messageInput', 'storeId')
    const client = getServiceClient()
    return client.sendMessage(SendMessageInput.clone(params)).toPromise()
}

/**
 * Submit customer actions response.
 * Required: parentId, responses, parentType, storeId (storeId added for Gateway AVP authorization)
 */
export async function submitCustomerActionsResponseListener (message: any): Promise<SubmitCustomerActionsResponseOutput> {
    const params = (message && message.data) || {}
    validateRequired(params, 'parentId', 'responses', 'parentType', 'storeId')
    const client = getServiceClient()
    return client.submitCustomerActionsResponse(SubmitCustomerActionsResponseInput.clone(params)).toPromise()
}
